/**
 * "Sorgente conversazione" del pannello assistente laterale: la stessa UI
 * (Chiedi all'AI, Lead/Cliente, Task commerciale, note) lavora su una chat
 * WhatsApp o su una conversazione Instagram/Messenger. Qui solo logica pura:
 * chiave delle note e corpo della richiesta a whatsapp-chat-ask.
 */

export interface AskHistoryItem {
  role: 'user' | 'assistant';
  content: string;
}

export type ConversationSource =
  | {
      kind: 'whatsapp';
      organizationId: string;
      chatJid: string;
      configId: string;
      clientId?: string | null;
      projectId?: string | null;
      dealId?: string | null;
    }
  | {
      kind: 'meta';
      organizationId: string;
      conversationId: string;
      /** Lead/Cliente collegato: la funzione lo rilegge comunque dalla conversazione. */
      clientId?: string | null;
    };

/** Chiave in whatsapp_chat_notes / whatsapp_chat_meta: il jid oppure 'meta:<id>'. */
export function notesKey(source: ConversationSource): string {
  return source.kind === 'whatsapp' ? source.chatJid : `meta:${source.conversationId}`;
}

/** Corpo per l'edge function whatsapp-chat-ask. history = domande e risposte precedenti. */
export function askRequestBody(source: ConversationSource, question: string, history: AskHistoryItem[]): Record<string, unknown> {
  const base = { organization_id: source.organizationId, question, history };
  if (source.kind === 'meta') {
    return { ...base, meta_conversation_id: source.conversationId };
  }
  return {
    ...base,
    chat_jid: source.chatJid,
    config_id: source.configId,
    client_id: source.clientId || undefined,
    project_id: source.projectId || undefined,
    deal_id: source.dealId || undefined,
  };
}

/**
 * Titolo di un Task commerciale nato da una risposta dell'assistente: la prima
 * riga utile, senza simboli di elenco o grassetti, al massimo 120 caratteri.
 */
export function taskTitleFromAnswer(answer: string): string {
  const line = answer
    .split('\n')
    .map((l) => l.replace(/\*\*/g, '').replace(/^\s*(?:#{1,3}\s+|[-•▸✓✅]\s*|\d+\.\s+)/, '').trim())
    .find((l) => l.length > 0) || '';
  return line.length > 120 ? `${line.slice(0, 117).trimEnd()}...` : line;
}

/** Stile del testo inserito nel messaggio: WhatsApp usa *grassetto*, Instagram/Messenger no. */
export type ChatTextStyle = 'whatsapp' | 'plain';

/** Riepilogo di un'offerta da scrivere nel messaggio ("In chat"). */
export function dealChatText(deal: { title: string; amount?: number | null }, statusLabel: string, style: ChatTextStyle): string {
  const amount = deal.amount && deal.amount > 0 ? `\nImporto: €${deal.amount.toLocaleString('it-IT')}` : '';
  const head = style === 'whatsapp' ? `📋 *${deal.title}*` : deal.title;
  return `${head}\nStato: ${statusLabel}${amount}`;
}

/** Stato di un progetto da scrivere nel messaggio ("In chat"). */
export function projectChatText(project: { name: string }, statusLabel: string, dueLabel: string | null, style: ChatTextStyle): string {
  const head = style === 'whatsapp' ? `📊 *${project.name}*` : project.name;
  return `${head}\nStato: ${statusLabel}${dueLabel ? `\nScadenza: ${dueLabel}` : ''}`;
}
